import { useEffect, useRef, useState } from "react";

const steps = [
  {
    num: "01",
    icon: "ri-play-circle-line",
    title: "ดูซีรีส์สั้น",
    subtitle: "Watch Short Drama",
    desc: "ดูซีรีส์ที่ชอบ ทุก 5 ตอนแรกของวันได้รับเหรียญทองตามระดับของคุณ ยิ่งระดับสูง ยิ่งได้มาก",
    reward: "5 ตอน/วัน รับเหรียญ",
    color: "from-red-500 to-orange-400",
  },
  {
    num: "02",
    icon: "ri-user-add-line",
    title: "ชวนเพื่อน",
    subtitle: "Invite Friends",
    desc: "แชร์ลิงก์ชวนเพื่อน รับโบนัสสนับสนุนการประกอบอาชีพและโบนัสช่วยเหลือการกลับมาทำงาน",
    reward: "โบนัสพิเศษ/คน",
    color: "from-orange-500 to-yellow-400",
  },
  {
    num: "03",
    icon: "ri-task-line",
    title: "ภารกิจประจำวัน",
    subtitle: "Daily Tasks & Check-in",
    desc: "เช็คอินทุกวัน ทำภารกิจประจำวัน กดไลก์ แชร์ สะสมเหรียญเพิ่มทุกวัน",
    reward: "เหรียญทุกวัน",
    color: "from-yellow-500 to-orange-400",
  },
  {
    num: "04",
    icon: "ri-bank-card-line",
    title: "ถอนเงิน",
    subtitle: "Cash Out",
    desc: "สะสมเหรียญครบ 3,000 เหรียญ ถอนได้เลย! ถอนผ่านกระเป๋า BEP20 เข้าบัญชี USDT หรือ USDC ของคุณ ภายใน 24–72 ชั่วโมง",
    reward: "3,000 เหรียญ = ฿100",
    color: "from-green-500 to-emerald-400",
  },
];

const scenarios = [
  {
    time: "พักเที่ยง",
    timeEn: "Lunch Break",
    icon: "ri-sun-line",
    desc: "ดูซีรีส์ระหว่างพักกินข้าว 30 นาที ได้เหรียญเพิ่มทุกวัน",
    img: "https://readdy.ai/api/search-image?query=Thai%20office%20worker%20woman%20eating%20lunch%20break%20watching%20smartphone%20short%20video%20drama%20relaxed%20happy%20warm%20restaurant%20background%20natural%20light&width=400&height=280&seq=scene-001&orientation=landscape",
  },
  {
    time: "เดินทาง",
    timeEn: "Commuting",
    icon: "ri-train-line",
    desc: "บนรถไฟฟ้า รถเมล์ เปิดดูซีรีส์ เวลาเดินทางไม่น่าเบื่ออีกต่อไป",
    img: "https://readdy.ai/api/search-image?query=Thai%20young%20person%20commuting%20BTS%20skytrain%20Bangkok%20watching%20phone%20short%20drama%20entertainment%20urban%20city%20lifestyle%20casual%20outfit&width=400&height=280&seq=scene-002&orientation=landscape",
  },
  {
    time: "ก่อนนอน",
    timeEn: "Before Sleep",
    icon: "ri-moon-line",
    desc: "ผ่อนคลายก่อนนอนด้วยซีรีส์สั้น ดูจบแล้วนอนหลับสบาย",
    img: "https://readdy.ai/api/search-image?query=Thai%20woman%20lying%20in%20bed%20cozy%20bedroom%20watching%20smartphone%20short%20drama%20before%20sleep%20relaxed%20comfortable%20warm%20bedroom%20lighting%20soft%20tones&width=400&height=280&seq=scene-003&orientation=landscape",
  },
];

export default function EarnMechanism() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="earn" className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-yellow-50 text-yellow-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            วิธีรับรางวัล
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            เริ่มรับเงินใน 4 ขั้นตอน
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            ง่ายมาก ไม่ต้องมีทักษะพิเศษ ใครก็ทำได้
          </p>
        </div>

        {/* Steps Flow */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`relative transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Connector arrow (desktop) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-10 -right-3 z-10 w-6 h-6 items-center justify-center text-orange-400">
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              )}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:-translate-y-1 transition-transform duration-300 h-full">
                {/* Number badge */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br ${step.color} mb-4`}>
                  <span className="text-white font-bold text-sm">{step.num}</span>
                </div>
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center mb-3">
                  <i className={`${step.icon} text-3xl text-gray-700`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                <p className="text-xs text-gray-400 mb-3">{step.subtitle}</p>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{step.desc}</p>
                <div className={`inline-block bg-gradient-to-r ${step.color} text-white text-xs font-bold px-3 py-1.5 rounded-full`}>
                  {step.reward}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Usage Scenarios */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            ดูได้ทุกที่ ทุกเวลา
          </h2>
          <p className="text-gray-500 text-lg">เปลี่ยนทุกช่วงเวลาว่างให้มีคุณค่า</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {scenarios.map((scene, i) => (
            <div
              key={scene.time}
              className={`relative rounded-2xl overflow-hidden group cursor-default transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${400 + i * 100}ms` }}
            >
              <div className="w-full h-56 overflow-hidden">
                <img
                  src={scene.img}
                  alt={scene.time}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <i className={`${scene.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-yellow-400 font-bold text-lg">{scene.time}</span>
                  <span className="text-white/50 text-sm">· {scene.timeEn}</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">{scene.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
